import CustomLabel from '@/components/custom-label';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Col, Form, Row, Typography, Upload, App, Input, FormInstance, Select } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '@/hooks/debounce.hook';
import {
  getListFood,
  watchListFood,
} from '@/redux/slices/food.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import React from 'react';

type MyUploadFile = {
  uid?: string;
  name?: string;
  status?: 'done' | 'uploading' | 'error' | 'removed';
  url?: string;
  sizeType?: string;
};

type Size = {
  sizeType: string;
  image?: string;
  dataBig: {
    keyFormula: number;
    id?: string;
    formulaType?: string;
    data: {
      keyComponent: number,
      id?: string;
      ingredient: { name: string, label: string },
      weight: { name: string },
      processingMethod: { name: string },
    }[]
  }[]
}

export const CreateDishSizeSection = ({
  size,
  sizeType,
  onEdit,
  onValuesChange,
  onRemoveValue,
  onImageChange,
  handleRemoveSize,
  onRemoveSaltFormula,
  form,
  listSaltDish,
  sizeRecordData,
}: {
  size: string;
  sizeType: string;
  onEdit: (targetKey: any, action: 'add' | 'remove' | 'update') => void,
  onValuesChange: (sizeType: string, data: any, keyFormula: number, keyComponent: number, formulaId?: string, componentId?: string) => void;
  onRemoveValue: (sizeType: string, keyFormula: number, keyComponent: number) => void;
  onImageChange: (sizeType: string, image: string) => void;
  handleRemoveSize: (sizeTypeToRemove: string) => void;
  onRemoveSaltFormula?: (sizeType: string, saltIndex: number) => void;
  form?: FormInstance<any>;
  listSaltDish: DishSalt[];
  sizeRecordData?: Size[];
}) => {
  const dispatch = useAppDispatch();
  const listFood = useAppSelector(watchListFood);
  const [saltRecipe, setSaltRecipe] = useState<Record<any, any>>({
    0: { 0: 0 }
  });

  const [fileList, setFileList] = useState<MyUploadFile[]>([]);
  const { modal } = App.useApp();
  const [removedSaltKeys, setRemovedSaltKeys] = useState<Set<number>>(new Set());
  const [removedSaltIds, setRemovedSaltIds] = useState<Map<number, string>>(new Map());
  const [removedComponentIds, setRemovedComponentIds] = useState<Map<string, string>>(new Map());

  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const isFirstRender = useRef(true);

  const handleSearchFood = (name?: FilterFood) => {
    dispatch(getListFood({ query: name, limit: 9999 }));
  }

  useEffect(() => {
    dispatch(getListFood({ limit: 9999 }));
  }, [dispatch]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    if (debouncedSearchValue.trim()) {
      handleSearchFood({ name: debouncedSearchValue });
    } else {
      dispatch(getListFood({ limit: 9999 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (sizeRecordData && form) {
      const fieldValues: Record<string, any> = {};
      const saltData: Record<number, Record<number, number>> = {
        0: { 0: 0 }
      };
      const imageList: { size: string; url: string }[] = [];
      sizeRecordData.forEach((size) => {
        if (sizeType == size.sizeType) {
          const sizeType = size.sizeType;
          imageList.push({ size: sizeType, url: size.image ?? '' });
          if (size.dataBig.length > 0) {
            size.dataBig.forEach((formula) => {
              const keyFormula = formula.keyFormula;
              if (removedSaltKeys.has(keyFormula)) {
                return;
              }
              saltData[keyFormula] = saltData[keyFormula] || {};

              formula.data.forEach((component, ingIndex) => {
                const ingredientId = component.ingredient?.name ?? "";
                saltData[keyFormula][ingIndex] = 0;

                fieldValues[`size_${sizeType}`] = fieldValues[`size_${sizeType}`] || {};
                fieldValues[`size_${sizeType}`][`salt_${keyFormula}`] =
                  fieldValues[`size_${sizeType}`][`salt_${keyFormula}`] || {};

                fieldValues[`size_${sizeType}`][`salt_${keyFormula}`][`component_${ingIndex}`] = {
                  name: {
                    value: ingredientId,
                    label: component.ingredient?.label ?? "",
                  },
                  weight: component.weight?.name ?? "",
                  preprocess: component.processingMethod?.name ?? "",
                };
              });
            });
          }
          else {
            fieldValues[`size_${sizeType}`] = {};
            fieldValues[`size_${sizeType}`][`salt_0`] = {};
            fieldValues[`size_${sizeType}`][`salt_0`][`component_0`] = {
              name: {
                value: '',
                label: ''
              },
              weight: '',
              preprocess: ''
            };
          }
        }

      });

      const matchedImage = imageList.find((img) => img.size === sizeType);

      if (matchedImage?.url) {
        setFileList(prev => {
          const exists = prev.some(item => item.sizeType === sizeType);
          if (exists) return prev;
          return [{
            uid: sizeType,
            name: 'image',
            status: 'done',
            url: matchedImage?.url,
            sizeType: sizeType,
          }];
        });
      }

      setSaltRecipe(saltData);

      form.setFieldsValue({
        ...fieldValues,
        size: {
          imageUrl: String(matchedImage?.url ?? '')
        },
      });

    } else {
      form?.setFieldsValue({
        [`size_${sizeType}`]: {
          [`salt_0`]: {
            [`component_0`]: {
              name: {
                value: '',
                label: ''
              },
              weight: '',
              preprocess: ''
            }
          }
        }
      });
    }
  }, [sizeRecordData, form, sizeType, removedSaltKeys]);

  const handleAddSaltRecipe = () => {
    const currentSaltCount = Object.keys(saltRecipe).length;

    if (currentSaltCount >= listSaltDish.length) {
      return;
    }
    // Tìm gap key đầu tiên 
    const existingKeys = Object.keys(saltRecipe).map(Number).sort((a, b) => a - b);
    let nextKey = 0;
    for (let i = 0; i < existingKeys.length; i++) {
      if (existingKeys[i] !== i) {
        nextKey = i;
        break;
      }
      nextKey = existingKeys[i] + 1;
    }
    
    // Lấy ID đã xóa nếu có để sử dụng khi xóa rồi tạo lại
    const reusedId = removedSaltIds.get(nextKey);
    
    // Lấy dữ liệu component của CTM đầu tiên (salt_0) sang salt_nextkey 
    const firstSaltData = { ...(saltRecipe[0] || { 0: 0 }) };
    const currentFormValues = form?.getFieldsValue();
    const firstSaltFormData = currentFormValues?.[`size_${sizeType}`]?.[`salt_0`];

    setSaltRecipe({
      ...saltRecipe,
      [nextKey]: firstSaltData,
    });
    // Xóa key vừa thêm khỏi danh sách key đã xóa
    setRemovedSaltKeys((prev) => {
      if (!prev.has(nextKey)) return prev;
      const next = new Set(prev);
      next.delete(nextKey);
      return next;
    });
    
    if (firstSaltFormData && Object.keys(firstSaltFormData).length > 0) {
      // Copy dữ liệu từ salt_0
      const duplicatedFormData: Record<string, any> = {};
      Object.keys(firstSaltFormData).forEach((componentKeyStr) => {
        const componentData = firstSaltFormData[componentKeyStr];
        if (componentData && (componentData.name?.value || componentData.weight || componentData.preprocess)) {
          duplicatedFormData[componentKeyStr] = {
            name: componentData.name || { value: '', label: '' },
            weight: componentData.weight || '',
            preprocess: componentData.preprocess || '',
          };
        }
      });

      if (Object.keys(duplicatedFormData).length > 0) {
        // Ghi dữ liệu duplicate vào Form để hiển thị
        form?.setFieldsValue({
          [`size_${sizeType}`]: {
            ...(currentFormValues?.[`size_${sizeType}`] || {}),
            [`salt_${nextKey}`]: duplicatedFormData,
          },
        });

        // Gọi onValuesChange cho từng component đã duplicate để đồng bộ state cha
        Object.keys(duplicatedFormData).forEach((componentKeyStr, index) => {
          const componentData = duplicatedFormData[componentKeyStr];
          const match = /component_(\d+)/.exec(componentKeyStr);
          const componentIndexNumber = match ? Number(match[1]) : index;
          
          // Lấy component ID đã xóa nếu có để tái sử dụng
          const componentKey = `${nextKey}_${componentIndexNumber}`;
          const reusedComponentId = removedComponentIds.get(componentKey);
          
          // Cập nhật state cha, với data từ CTM1 nhưng ID cũ
          onValuesChange(
            sizeType,
            {
              ingredient: componentData.name?.value ? {
                name: componentData.name.value,
                label: componentData.name.label,
              } : {},
              weight: componentData.weight ? { name: componentData.weight } : {},
              processingMethod: componentData.preprocess ? { name: componentData.preprocess } : {},
            },
            nextKey,
            componentIndexNumber,
            reusedId,
            reusedComponentId
          );
        });
      }
    }
  };

  const handleRemoveSaltRecipe = (key: number) => {
    // Lưu ID của formula và tất cả component IDs trước khi xóa
    const currentSize = sizeRecordData?.find(s => s.sizeType === sizeType);
    const formula = currentSize?.dataBig.find(f => f.keyFormula === key);
    
    if (formula?.id) {
      setRemovedSaltIds(prev => new Map(prev).set(key, formula.id!));
      
      formula.data.forEach(component => {
        if (component.id) {
          const componentKey = `${key}_${component.keyComponent}`;
          setRemovedComponentIds(prev => new Map(prev).set(componentKey, component.id!));
        }
      });
    }

    const newSaltRecipe = { ...saltRecipe };
    delete newSaltRecipe[key];
    setSaltRecipe(newSaltRecipe);
    setRemovedSaltKeys((prev) => new Set(prev).add(key));

    const currentFormValues = form?.getFieldsValue();
    if (currentFormValues?.[`size_${sizeType}`]?.[`salt_${key}`]) {
      const nextSizeValues = { ...(currentFormValues?.[`size_${sizeType}`] || {}) };
      delete nextSizeValues[`salt_${key}`];
      form?.setFieldsValue({
        [`size_${sizeType}`]: nextSizeValues,
      });
    }

    if (onRemoveSaltFormula) {
      onRemoveSaltFormula(sizeType, key);
    }
  };

  const handleAddComponent = (saltIndex: number) => {
    const currentComponents = saltRecipe[saltIndex] || {};
    const keys = Object.keys(currentComponents).map(Number);
    const nextKey = keys.length > 0 ? Math.max(...keys) + 1 : 0;

    // Lấy component ID đã xóa nếu có để tái sử dụng
    const componentKey = `${saltIndex}_${nextKey}`;
    const reusedComponentId = removedComponentIds.get(componentKey);

    setSaltRecipe({
      ...saltRecipe,
      [saltIndex]: {
        ...currentComponents,
        [nextKey]: 0
      }
    });

    onValuesChange(
      sizeType,
      { ingredient: {} },
      saltIndex,
      nextKey,
      undefined,
      reusedComponentId
    );

    form?.setFieldsValue({
      [`size_${sizeType}`]: {
        [`salt_${saltIndex}`]: {
          [`component_${nextKey}`]: {
            name: {
              value: '',
              label: ''
            },
            weight: '',
            preprocess: ''
          }
        }
      }
    });
  };

  const handleRemoveComponent = (sizeType: string, saltIndex: number, removeIndex: number) => {
    // Lưu component ID trước khi xóa để tái sử dụng
    const currentSize = sizeRecordData?.find(s => s.sizeType === sizeType);
    const formula = currentSize?.dataBig.find(f => f.keyFormula === saltIndex);
    const component = formula?.data.find(c => c.keyComponent === removeIndex);
    
    if (component?.id) {
      const componentKey = `${saltIndex}_${removeIndex}`;
      setRemovedComponentIds(prev => new Map(prev).set(componentKey, component.id!));
    }

    const updatedSalt = { ...saltRecipe[saltIndex] };
    delete updatedSalt[removeIndex];

    setSaltRecipe({
      ...saltRecipe,
      [saltIndex]: updatedSalt
    });
    onRemoveValue(sizeType, saltIndex, removeIndex);
  };

  // useEffect(() => {
  //   setSaltRecipe({ 0: { 0: 0 } });
  // }, [size]);

  const confirmDeleteAdmin = () => {
    modal.confirm({
      title: `Bạn có chắc chắn xoá cỡ món ăn ${size}?`,
      content: (
        <em>
          Dữ liệu không thể khôi phục
        </em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => {
        onEdit(sizeType, 'remove');
        handleRemoveSize(sizeType);
      }
    });
  };

  const beforeUpload = (file: File, sizeType: string) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const base64 = reader.result as string;

      onImageChange(sizeType, base64);

      setFileList(prev => {
        const filtered = prev.filter(item => item.sizeType !== sizeType);
        return [
          ...filtered,
          {
            sizeType,
            url: base64,
            name: file.name
          }
        ];
      });
    };
    return false;
  };

  return (
    <div css={containerStyles}>
      <Row align={'middle'} justify={'end'} gutter={8}>
        <Col>
          <Button
            icon={<EditOutlined />}
            iconPosition='start'
            css={editButtonStyles}
            onClick={() => onEdit(sizeType, 'update')}
          >
            Chỉnh sửa cỡ món {size}
          </Button>
        </Col>
        <Col>
          <Button
            type='primary'
            icon={<DeleteOutlined />}
            iconPosition='start'
            danger
            onClick={confirmDeleteAdmin}
          >
            Xóa cỡ món
          </Button>
        </Col>
      </Row>
      <Row css={boxStyles}>
        <Typography css={subTitleStyles}>Ảnh cỡ món</Typography>

        <Form.Item
          name={['size', 'imageUrl']}
          label={CustomLabel({ text: 'Ảnh cỡ món', isRequired: true })}
          rules={[{ required: true, message: 'Vui lòng chọn ảnh!' }]}
        >
          <Upload
            beforeUpload={(file) => beforeUpload(file, sizeType)}
            listType='picture'
            accept='.png, .jpeg, .jpg'
            fileList={
              fileList
                .filter(item => item.sizeType === sizeType)
                .map(item => ({
                  uid: sizeType,
                  name: item.name || 'image',
                  status: 'done',
                  url: item.url,
                }))
            }
            onRemove={() => {
              onImageChange(sizeType, '');
              setFileList(prev => prev.filter(item => item.sizeType !== sizeType));
            }}
          >
            <Button icon={<UploadOutlined />} iconPosition='start'>
              Chọn để tải lên hình ảnh
            </Button>
          </Upload>
        </Form.Item>
      </Row>
      {Object.keys(saltRecipe).map((key, saltIndex) => {
        const actualKey = Number(key);

        return (
        <React.Fragment key={`salt-${key}`}>
          <Row css={boxStyles}>
            <Row
              gutter={16}
              justify={'space-between'}
              align={'middle'}
              css={titleStyles}
            >
              <Typography css={subTitleStyles}>
                {listSaltDish[actualKey]?.formulaType ?? `Công thức muối ${actualKey + 1}`}
              </Typography>

              {saltIndex > 0 && (
                <Button
                  type='primary'
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => handleRemoveSaltRecipe(Number(key))}
                />
              )}
            </Row>

            {Object.keys(saltRecipe[key]).map((componentKey) => {
              const index = Number(componentKey);
              return (
              <Row gutter={16} key={componentKey}>
                <Col span={8}>
                  <Form.Item
                    name={[
                      `size_${sizeType}`,
                      `salt_${actualKey}`,
                      `component_${index}`,
                      'name'
                    ]}
                    label={CustomLabel({
                      text: `Thành phần ${index + 1}`,
                      isRequired: true
                    })}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || !value.label) {
                            return Promise.reject('Vui lòng chọn thành phần!');
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >

                    <Select
                      showSearch
                      allowClear
                      placeholder="Tìm kiếm và chọn thành phần"
                      filterOption={false}
                      onSearch={(value) => {
                        setSearchValue(value);
                      }}
                      onFocus={() => {
                        if (listFood.length === 0) {
                          handleSearchFood();
                        }
                      }}
                      options={listFood.map((food) => ({
                        value: food.ID,
                        label: food.NAME,
                      }))}
                      onChange={(option, label: any) => {
                        onValuesChange(
                          sizeType,
                          { ingredient: { name: option, label: label.label } },
                          actualKey,
                          index,
                          undefined,
                          undefined
                        );
                      }}
                    />

                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={[
                      `size_${sizeType}`,
                      `salt_${actualKey}`,
                      `component_${index}`,
                      'weight'
                    ]}
                    label={CustomLabel({
                      text: 'Khối lượng',
                      isRequired: true
                    })}
                    rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
                    normalize={(value) => value.replace(/[^\d,]/g, '')}
                  >
                    <Input
                      placeholder='Nhập khối lượng của thành phần trong món ăn'
                      onChange={(e) => onValuesChange(sizeType, {
                        weight: { name: e.target.value }
                      },
                        actualKey,
                        index,
                        undefined,
                        undefined
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    name={[
                      `size_${sizeType}`,
                      `salt_${actualKey}`,
                      `component_${index}`,
                      'preprocess'
                    ]}
                    label={CustomLabel({
                      text: 'Sơ chế'
                    })}
                  >
                    <Input
                      placeholder='Nhập cách sơ chế thực phẩm'
                      onChange={(e) => onValuesChange(sizeType, {
                        processingMethod: { name: e.target.value }
                      },
                        actualKey,
                        index,
                        undefined,
                        undefined
                      )}
                    />
                  </Form.Item>
                </Col>

                <Col span={1}>
                  <Button type='primary' icon={<DeleteOutlined />} danger
                    disabled={Object.keys(saltRecipe[key]).length === 1}
                    onClick={() =>
                      handleRemoveComponent(
                        sizeType,
                        actualKey,
                        Number(index)
                      )
                    } />
                </Col>
              </Row>
              );
            })}

            <Button
              icon={<PlusOutlined />}
              iconPosition='start'
              onClick={() =>
                handleAddComponent(actualKey)
              }
              css={addButtonStyles}
              style={{ border: 'none' }}
            >
              Thêm thành phần khác
            </Button>
          </Row>
        </React.Fragment>
        );
      })}

      <Button
        type='default'
        icon={<PlusOutlined />}
        iconPosition='start'
        onClick={handleAddSaltRecipe}
        css={addButtonStyles}
      >
        Thêm công thức muối
      </Button>
    </div>
  );
};

const containerStyles = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const editButtonStyles = css`
  background-color: var(--success-color);
  color: white;

  &:hover {
    background-color: var(--success-color) !important;
    color: white !important;
    border-color: var(--success-color) !important;
  }
`;

const boxStyles = css`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 1rem;

  .ant-form-item {
    .ant-form-item-label {
      display: flex;
      align-items: center;
      margin-right: 2rem;
      text-align: start;

      label {
        flex: 1;

        &::after,
        &::before {
          display: none !important;
        }
      }
    }
    .ant-form-item-control-input {
      .ant-input {
        font-size: 14px !important;
        padding: 0.8rem 1.2rem !important;
      }
    }

    .ant-form-item-control-input-content .ant-input {
      height: 3.2rem !important;
    }
  }
`;

const titleStyles = css`
  padding: 0.7rem;
`;

const subTitleStyles = css`
  font-family: Roboto;
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0%;
  color: rgba(100, 100, 100, 1);
`;

const addButtonStyles = css`
  align-self: start;
  color: var(--menu-active-color);
  border-color: var(--menu-active-color);
  box-shadow: none;
`;
